<template>
    <div
        class="relative overflow-hidden"
        :class="[
            component === 'author' || isBird ? 'rounded-3xl rounded-br-none' : 'rounded-3xl',
            component === 'latest' || component === 'author' ? ' bg-white ' : ' bg-light '
        ]"
    >
        <a
            :href="item.link"
            :data-track="isBird ? 'Card bird' : 'Card article'"
            :class="[
                'group relative block h-full',
                component === 'author' ? 'rounded-3xl rounded-br-none overflow-hidden transition-colors hover:bg-light' : '',
                isBird ? 'rounded-3xl rounded-br-none overflow-hidden' : ''
            ]"
        >
            <span v-if="item.categories && item.categories.length" class="absolute top-0 right-0 z-10 inline-flex px-3 py-2 rounded-tr-3xl bg-dark uppercase text-mdx text-light font-medium tracking-wider">
                <span v-html="item.categories[0]" class="relative top-0.5">
                </span>
            </span>

            <div class="relative block w-full overflow-hidden">
                <v-lazy-image
                    v-if="item.image"
                    :class="[
                        'w-full h-56 select-none object-cover',
                        component === 'author' || isBird ? 'transition-transform duration-300 group-hover:scale-105' : ''
                    ]"
                    use-picture
                    width="380"
                    height="224"
                    draggable="false"
                    :alt="`${item.name} image`"
                    :src="`${item.image.url}?tr=w-380,h-224`"
                    :src-placeholder="`${$settings.theme_url}/assets/images/default-placeholder.png?tr=w-380,h-224`"
                >
                <!-- One column between 640 and 899px in the news grid and the birds archive, so the card is up to 868px wide -->
                <source
                    v-if="oneColumnOnTablets"
                    media="(min-width: 640px) and (max-width: 899px)"
                    sizes="calc(100vw - 32px)"
                    :srcset="`${item.image.url}?tr=w-760,h-448 760w, ${item.image.url}?tr=w-1560,h-920 1560w`"
                />
                <source
                    :srcset="
                    `
                    ${item.image.url}?tr=w-380,h-224,
                    ${item.image.url}?tr=w-760,h-448 2x
                    `
                    "
                />
                </v-lazy-image>

                <img v-else class="w-full select-none object-cover" :src="`${$settings.theme_url}/assets/images/default-placeholder.png?tr=w-380,h-224`" alt="default placeholder" loading="lazy">

                <div class="absolute -bottom-0.5 left-0 z-10 w-full">
                    <div class="-mr-1">
                        <svg
                            class="h-auto"
                            :class="[
                                component === 'author'
                                    ? 'fill-white transition-colors group-hover:fill-light'
                                    : component === 'latest' ? 'fill-white' : 'fill-light'
                            ]"
                            viewBox="0 0 384 33"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M0 1.80893V32.7251H384V3.77203C369.413 10.3151 342.427 21.929 306.872 26.8363C273.676 31.418 257.094 29.7807 236.308 29.29C215.529 28.7994 174.496 21.929 161.368 18.4939C148.239 15.0588 91.8974 4.26258 68.9231 1.80893C50.5436 -0.153993 15.3162 0.991044 0 1.80893Z" />
                        </svg>
                    </div>
                </div>
            </div>

            <div
                class="relative pb-6 pt-6"
                :class="component === 'author' ? 'transition-colors group-hover:bg-light' : ''"
            >
                <h3 :class="[
                    'flex w-full text-lg lg:text-xlx lg:leading-8',
                    isBird ? 'min-h-0' : 'min-h-20 lg:min-h-24'
                ]">
                    <span v-html="item.title" class="w-full pl-6 pr-12"></span>
                </h3>

                <!-- Description/subtitle for bird cards -->
                <p v-if="isBird && item.description" class="pl-6 pr-20 mt-2 text-base lg:text-lg font-regular text-dark line-clamp-2">
                    {{ item.description }}
                </p>

                <span
                    v-if="component !== 'author' && !isBird"
                    class="absolute bottom-6 left-0 w-full flex justify-end pr-6"
                >
                    <img width="18" height="20" :src="`${$settings.theme_url}/assets/images/icons/arrow-right-gold.svg`" alt="right arrow" loading="lazy">
                </span>
            </div>

            <!-- Curved corner with arrow for author component -->
            <div v-if="component === 'author'" class="absolute right-0 bottom-0 flex justify-center items-center w-22.75 h-23">
                <svg class="absolute -bottom-0.5 right-[-1px] fill-white" width="91" height="92" viewBox="0 0 91 92" xmlns="http://www.w3.org/2000/svg">
                    <clipPath id="clipCircle">
                        <circle cx="59" cy="59" r="22"></circle>
                    </clipPath>
                    <path id="corner" d="M84.5732 13.6802C89.5778 9.16883 90.9672 3.25002 91.0001 0.500244L91.0001 18.9901H72.5103C74.1582 18.9901 79.6293 18.1367 84.5732 13.6802ZM12.0614 86.189C17.0661 81.6776 18.4554 75.7588 18.4884 73.009V91.4989L-0.00146484 91.4989C1.64648 91.4989 7.11762 90.6455 12.0614 86.189ZM91.0001 91.4999V18.9901L55.9411 18.9906C35.2579 18.9906 18.4909 35.7576 18.4909 56.4408L18.4909 91.4999H91.0001Z"></path>
                    <use href="#corner" clip-path="url(#clipCircle)" class="fill-white transition-colors group-hover:fill-default"></use>
                    <path d="M67.364 59.3777L61.114 65.6277C60.6453 66.1355 59.825 66.1355 59.3562 65.6277C58.8484 65.1589 58.8484 64.3386 59.3562 63.8699L63.4578 59.7292H51.5046C50.8015 59.7292 50.2546 59.1824 50.2546 58.4792C50.2546 57.8152 50.8015 57.2292 51.5046 57.2292H63.4578L59.3562 53.1277C58.8484 52.6589 58.8484 51.8386 59.3562 51.3699C59.825 50.8621 60.6453 50.8621 61.114 51.3699L67.364 57.6199C67.8718 58.0886 67.8718 58.9089 67.364 59.3777Z" fill="#2C2C2B"></path>
                </svg>
            </div>

            <!-- Curved corner with arrow for bird cards -->
            <div v-if="isBird" class="absolute right-0 bottom-0 flex justify-center items-center w-22.75 h-23">
                <svg class="absolute -bottom-0.5 right-[-1px] fill-white" width="91" height="92" viewBox="0 0 91 92" xmlns="http://www.w3.org/2000/svg">
                    <clipPath :id="`clipCircle-bird-${item.id}`">
                        <circle cx="59" cy="59" r="22"></circle>
                    </clipPath>
                    <path :id="`corner-bird-${item.id}`" d="M84.5732 13.6802C89.5778 9.16883 90.9672 3.25002 91.0001 0.500244L91.0001 18.9901H72.5103C74.1582 18.9901 79.6293 18.1367 84.5732 13.6802ZM12.0614 86.189C17.0661 81.6776 18.4554 75.7588 18.4884 73.009V91.4989L-0.00146484 91.4989C1.64648 91.4989 7.11762 90.6455 12.0614 86.189ZM91.0001 91.4999V18.9901L55.9411 18.9906C35.2579 18.9906 18.4909 35.7576 18.4909 56.4408L18.4909 91.4999H91.0001Z"></path>
                    <use :href="`#corner-bird-${item.id}`" :clip-path="`url(#clipCircle-bird-${item.id})`" class="fill-default transition-colors lg:group-hover:fill-default-light"></use>
                    <path d="M67.364 59.3777L61.114 65.6277C60.6453 66.1355 59.825 66.1355 59.3562 65.6277C58.8484 65.1589 58.8484 64.3386 59.3562 63.8699L63.4578 59.7292H51.5046C50.8015 59.7292 50.2546 59.1824 50.2546 58.4792C50.2546 57.8152 50.8015 57.2292 51.5046 57.2292H63.4578L59.3562 53.1277C58.8484 52.6589 58.8484 51.8386 59.3562 51.3699C59.825 50.8621 60.6453 50.8621 61.114 51.3699L67.364 57.6199C67.8718 58.0886 67.8718 58.9089 67.364 59.3777Z" fill="#3B3C4A"></path>
                </svg>
            </div>
        </a>
    </div>
</template>
<script>
import VLazyImage from "./../plugins/v-lazy-image/dist/v-lazy-image";

export default {
    components: {
        VLazyImage,
    },
    props: {
        item: {
            type: Object,
            default: () => ({}),
        },
        component: {
            type: String,
            default: "",
        },
        // False in the related blocks, which are two columns from 640px
        oneColumnOnTablets: {
            type: Boolean,
            default: true,
        },
    },
    computed: {
        isBird() {
            return this.item.type === 'bird';
        },
    },
    created() {
        // console.log(this.item)
    },
}
</script>
